import { Controller } from "@hotwired/stimulus"
import { Chip } from "@coreui/coreui-pro"
import { setSelectedValues } from "../shared/multi_select"

const PREVIEW_LIMIT = 3
const INPUT_TYPES = new Set(["text", "search", "email", "tel", "url", "number", "date", "month", "week", "time", "datetime-local", "checkbox", "radio"])

// This is an editable preview of the form, not a second source of query params.
export default class extends Controller {
  static targets = ["list", "status"]
  static values = { removeLabel: String, moreLabel: String, lessLabel: String, initialSignature: String }

  connect() {
    this.form = this.element.closest("form")
    if (!this.form) return

    this.expanded = new Set()
    this.chips = []
    // The original query must survive a Turbo snapshot of an edited form.
    if (!this.hasInitialSignatureValue) this.initialSignatureValue = this.signature()
    this.initialValues = this.initialSignatureValue
    this.abortController = new AbortController()
    const options = { signal: this.abortController.signal }
    this.form.addEventListener("input", () => this.scheduleRender(), options)
    this.form.addEventListener("change", () => this.scheduleRender(), options)
    this.form.addEventListener("reset", () => {
      this.scheduleRender()
    }, options)
    // Cascading and remote pickers can replace labels or disable fields without
    // changing their values. Ignore our own chip DOM when observing the form.
    this.observer = new MutationObserver((records) => {
      if (records.some((record) => !this.element.contains(record.target))) this.scheduleRender()
    })
    this.observer.observe(this.form, {
      subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ["disabled", "readonly", "required", "label", "selected", "value", "data-report-filter-empty-value"]
    })
    this.render()
  }

  get fields() {
    return Array.from(this.form.elements).filter((field) => field.name && !field.matches(":disabled") &&
      (field.tagName === "SELECT" || (field.tagName === "INPUT" && INPUT_TYPES.has(field.type))))
  }

  scheduleRender() {
    cancelAnimationFrame(this.renderFrame)
    this.renderFrame = requestAnimationFrame(() => this.render())
  }

  signature() {
    // Ignore unnamed CoreUI search inputs and include other report settings.
    return JSON.stringify(Array.from(new FormData(this.form)))
  }

  render() {
    if (!this.abortController || this.abortController.signal.aborted) return

    const fields = this.fields
    const signature = this.signature()
    const state = JSON.stringify([signature, Array.from(this.expanded), fields.map((field) => [
      field.name, this.fieldLabel(field), field.required, field.readOnly, field.dataset.reportFilterEmptyValue,
      field.tagName === "SELECT" ? Array.from(field.options, (option) =>
        [option.value, option.textContent, option.selected, option.matches(":disabled")]) : [field.value, field.checked]
    ])])
    // Blurring an edited input fires change after input. Replacing unchanged
    // chips here can detach the very chip receiving keyboard or pointer focus.
    if (state === this.renderedState) return
    this.renderedState = state
    this.disposeChips()
    this.listTarget.replaceChildren()
    for (const field of fields) {
      if (field.tagName !== "SELECT") {
        this.addInputChip(field)
        continue
      }
      // Some reports use a nonblank value for "all". Only explicitly marked
      // controls treat that value as empty; keep the original Rails params.
      const selected = Array.from(field.selectedOptions).filter((option) =>
        option.value !== "" && option.value !== field.dataset.reportFilterEmptyValue)
      const label = this.fieldLabel(field)
      const visible = this.expanded.has(field.name) ? selected : selected.slice(0, PREVIEW_LIMIT)
      for (const option of visible) this.addChip(field, option, label, selected)

      if (selected.length > PREVIEW_LIMIT) {
        const toggle = document.createElement("button")
        toggle.type = "button"
        toggle.className = "btn btn-sm btn-link p-0"
        toggle.textContent = this.expanded.has(field.name) ? this.lessLabelValue :
          this.moreLabelValue.replace("%{count}", selected.length - PREVIEW_LIMIT)
        toggle.setAttribute("aria-label", `${label}：${toggle.textContent}`)
        toggle.setAttribute("aria-expanded", String(this.expanded.has(field.name)))
        toggle.addEventListener("click", () => {
          this.expanded.has(field.name) ? this.expanded.delete(field.name) : this.expanded.add(field.name)
          this.render()
          Array.from(this.listTarget.querySelectorAll("[data-field-toggle]")).find((button) => button.dataset.fieldToggle === (field.id || field.name))?.focus()
        })
        toggle.dataset.fieldToggle = field.id || field.name
        this.listTarget.append(toggle)
      }
    }
    this.statusTarget.hidden = signature === this.initialValues
    this.element.hidden = !this.listTarget.childElementCount && this.statusTarget.hidden
    if (this.focusAfterRender) {
      const field = this.focusAfterRender
      this.focusAfterRender = null
      const search = Array.from(this.form.querySelectorAll("input")).find((input) => input.id === `search-${field.id}`)
      const focusTarget = search || field
      focusTarget.focus()
    }
  }

  fieldLabel(field) {
    const labelledBy = field.getAttribute("aria-labelledby")?.split(/\s+/)
      .map((id) => document.getElementById(id)?.textContent || "").join(" ").trim()
    const label = Array.from(field.labels || []).find((label) => label.textContent.trim()) ||
      field.closest(".form-check, .form-group")?.querySelector("label")
    let placeholder = field.getAttribute("placeholder")
    if (!placeholder && field.dataset.coreuiConfig) {
      placeholder = JSON.parse(field.dataset.coreuiConfig).placeholder
    }
    return (labelledBy || field.getAttribute("aria-label") || label?.textContent?.trim() || placeholder || field.name)
      .trim().replace(/[：:]\s*$/, "")
  }

  addChip(field, option, label, selected) {
    const name = option.textContent.trim()
    const duplicate = Array.from(field.options).filter((item) => item.textContent.trim() === name).length > 1
    const text = `${label}：${name}${duplicate ? ` (${option.value})` : ""}`
    const emptyOption = Array.from(field.options).find((item) =>
      item.value === (field.dataset.reportFilterEmptyValue ?? "") && !item.matches(":disabled"))
    const canClear = field.multiple || (!field.required && emptyOption)
    const lastRequired = field.required && selected.filter((item) => !item.matches(":disabled")).length <= 1
    this.createChip(field, option.value, text, canClear && !option.matches(":disabled") && !lastRequired, () => {
      const remaining = selected.filter((item) => item !== option).map((item) => item.value)
      setSelectedValues(field, !field.multiple && emptyOption ? [emptyOption.value] : remaining)
    })
  }

  addInputChip(field) {
    const toggle = field.type === "checkbox" || field.type === "radio"
    if (toggle ? !field.checked : !field.value) return

    const label = this.fieldLabel(field)
    const text = toggle ? label : `${label}：${field.value}`
    const removable = !field.required && !field.readOnly && field.type !== "radio"
    this.createChip(field, field.value, text, removable, () => {
      if (toggle) field.checked = false
      else field.value = ""
      // Preserve existing dependent-control actions as well as native form data.
      field.dispatchEvent(new Event("input", { bubbles: true }))
      field.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  createChip(field, value, label, removable, remove) {
    const element = document.createElement("span")
    element.className = "chip chip-sm chip-outline chip-primary"
    element.dataset.filterName = field.name
    element.dataset.filterValue = value
    const text = document.createElement("span")
    text.className = "chip-label"
    text.textContent = label
    element.title = label
    element.append(text)
    this.listTarget.append(element)

    this.chips.push(new Chip(element, {
      removable,
      ariaRemoveLabel: `${this.removeLabelValue} ${text.textContent}`
    }))
    element.addEventListener("remove.coreui.chip", (event) => {
      // Let the form change render and dispose all chips together.
      event.preventDefault()
      if (!removable) return
      this.focusAfterRender = field
      remove()
    })
  }

  disposeChips() {
    this.chips?.forEach((chip) => chip.dispose())
    this.chips = []
  }

  unmount() {
    this.abortController?.abort()
    this.observer?.disconnect()
    cancelAnimationFrame(this.renderFrame)
    this.renderedState = null
    this.focusAfterRender = null
    this.disposeChips()
    this.listTarget.replaceChildren()
  }

  disconnect() {
    this.unmount()
  }
}
